import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { adminDeletePromoCode, adminListPromoCodes, adminUpdatePromoCode, products } from '../../api/pricing';
import { formatINR } from '../../shared/data/common';
import Icon from '../../shared/components/Icons';
import AdminShell, { useAdminAccess } from './AdminShell';
import { describeDiscount, promoKinds, statusInfo, toPromoForm, toPromoRequest } from './promoForm';

const filters = [['', 'All'], ['Active', 'Active'], ['Scheduled', 'Scheduled'], ['Expired', 'Expired'], ['Inactive', 'Off']];
const shortDate = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

export default function AdminPromoCodesPage() {
  const { auth } = useAuth();
  const hasAccess = useAdminAccess();
  const { state } = useLocation();
  const [list, setList] = useState(null);
  const [filter, setFilter] = useState('');
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      setError('');
      setList(await adminListPromoCodes(auth.accessToken));
    } catch (requestError) {
      setError(requestError.message);
    }
  }, [auth?.accessToken]);

  useEffect(() => {
    if (hasAccess) load();
  }, [hasAccess, load]);

  const run = async (promo, action) => {
    setBusyId(promo.id);
    setError('');
    try {
      await action();
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusyId(null);
    }
  };

  const toggle = (promo) => run(promo, () => adminUpdatePromoCode(auth.accessToken, promo.id,
    toPromoRequest({ ...toPromoForm(promo), isActive: !promo.isActive })));

  const remove = (promo) => {
    if (!window.confirm(`Delete promo code ${promo.code}?`)) return;
    run(promo, () => adminDeletePromoCode(auth.accessToken, promo.id));
  };

  const shown = list?.filter((p) => !filter || p.status === filter || (filter === 'Expired' && p.status === 'UsedUp'));

  return (
    <AdminShell
      title="Promo codes"
      subtitle="Discounts customers enter at checkout on flights, hotels, sightseeing, packages, transfers and insurance."
      actions={<Link to="/admin/promo-codes/new" className="btn btn-primary btn-sm"><Icon name="tag" size={16} /> New promo code</Link>}
    >
      {state?.message && <p className="api-message success" role="status">{state.message}</p>}
      <div className="underline-tabs admin-tabs">
        {filters.map(([key, label]) => (
          <button key={key || 'all'} className={filter === key ? 'active' : ''} onClick={() => setFilter(key)}>{label}</button>
        ))}
      </div>
      {error && <p className="api-message error" role="alert">{error}</p>}
      {!list && !error && <p role="status">Loading promo codes…</p>}
      {shown && (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead><tr><th>Code</th><th>Offer</th><th>Type</th><th>Products</th><th>Discount</th><th>Valid</th><th>Used</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {shown.map((p) => (
                <tr key={p.id}>
                  <td><b>{p.code}</b>{!p.showOnSite && <div className="muted small">Hidden</div>}</td>
                  <td className="admin-wrap">{p.title}</td>
                  <td>{p.kind === 'Festival' ? `Festival · ${p.festivalName}` : promoKinds[p.kind]?.label || p.kind}</td>
                  <td className="admin-wrap">{p.products.length === products.length ? 'All products' : p.products.join(', ')}</td>
                  <td>{describeDiscount(p)}{p.minBookingAmount ? <div className="muted small">Min. {formatINR(p.minBookingAmount)}</div> : null}</td>
                  <td>{shortDate(p.validFrom)} – {shortDate(p.validUntil)}</td>
                  <td>
                    {p.redemptionCount}{p.usageLimit ? ` / ${p.usageLimit}` : ''}
                    {p.totalDiscount > 0 && <div className="muted small">{formatINR(p.totalDiscount)} given</div>}
                  </td>
                  <td><span className={`status-pill ${p.status === 'Active' ? 'live' : p.status.toLowerCase()}`}>{statusInfo[p.status] || p.status}</span></td>
                  <td className="admin-row-actions">
                    <Link className="btn btn-outline btn-sm" to={`/admin/promo-codes/${p.id}/edit`}>Edit</Link>
                    <button className="btn btn-outline btn-sm" disabled={busyId === p.id} onClick={() => toggle(p)}>
                      {p.isActive ? 'Turn off' : 'Turn on'}
                    </button>
                    {p.redemptionCount === 0 && (
                      <button className="btn btn-outline btn-sm danger" disabled={busyId === p.id} onClick={() => remove(p)}>Delete</button>
                    )}
                  </td>
                </tr>
              ))}
              {!shown.length && <tr><td colSpan="9">{list.length ? 'No promo codes match this filter.' : 'No promo codes yet. Use “New promo code” to add one.'}</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </AdminShell>
  );
}
